import type { EventDetails } from '@/api/Helpers'
import type { MembershipRules } from '@/api/Membership'
import { cartParams, ticketsPayload } from '@/helpers/BuyMembershipHelpers'
import { mapEntries } from '@/helpers/DictHelpers'
import environment from '@/helpers/Environment'
import { groupBy, indexBy } from '@/helpers/IndexHelpers'
import { reserveTickets } from '@/state/Reserve'
import type { UntimedItem } from '@/store/CartItem'
import store from '@/store/store'
import moment from 'moment-timezone'
import type { Moment } from 'moment-timezone/moment-timezone'

export interface MembershipSummary {
  benefits: MemberBenefits
  validFrom: Moment
  validTo: Moment
}

export interface MembershipWithMoments extends MembershipEntity {
  benefits: MemberBenefits
  validToMoment: Moment
  validFromMoment: Moment
}

export function currentMembership(): MembershipSummary | void {
  const now = moment()
  return membershipOnDate(now)
}

export function membershipOnDate(date: Moment): MembershipSummary | void {
  const memberships = allMemberships()

  return memberships.find((membership) => {
    const from = membership.validFrom
    const to = membership.validTo
    return date.isBetween(from, to, 'day', '[]')
  })
}

export function inCartMembership(): MembershipSummary | void {
  const gifting = store.getters['Cart/giftee']
  const items = store.getters['Cart/cartItems']
  const item = items.find((item) => item.isMembershipEvent)

  if (item && !gifting) {
    return cartItemToMembership(item)
  }
}

export function purchasedMemberships(): MembershipSummary[] {
  const memberships = store.getters['Member/memberships'] as MembershipWithMoments[]
  return memberships.map((membership) => ({
    validTo: membership.validToMoment,
    validFrom: membership.validFromMoment,
    benefits: membership.member_event_purchase_limit,
  }))
}

export function isCurrentMember(): boolean {
  const memberships = purchasedMemberships()
  return memberships.length > 0
}

function allMemberships(): MembershipSummary[] {
  const inCart = inCartMembership()
  // Always prioritise an in-cart membership over any purchased memberships so that the customer can not unnecessarily
  // purchase a membership in order to get benefits that they already have.
  return inCart ? [inCart] : purchasedMemberships()
}

function cartItemToMembership(cartItem: UntimedItem): MembershipSummary | void {
  const rules = membershipRules()

  if (rules) {
    const limits: MemberBenefits = {}

    for (const lineItem of cartItem.types) {
      // cartItem can contain ticket types where type.group.handler === 'codes' which are not relevant to member benefits.
      if (lineItem.group.handler === 'membership') {
        const name = rules[lineItem.group.id][lineItem.type.id].member_event_ticket_type
        if (limits[name]) {
          limits[name] += lineItem.ticketCount
        } else {
          limits[name] = lineItem.ticketCount
        }
      }
    }

    return {
      benefits: limits,
      validTo: cartItem.validToMoment,
      validFrom: cartItem.validFromMoment,
    }
  }
}

function membershipRules(): Dict<Dict<MembershipRuleEntity>> | void {
  const rules = store.state['membershipRules'] as MembershipRuleEntity[]
  if (rules.length) {
    const byGroup = groupBy('ticket_group_id', rules)
    return mapEntries(byGroup, (group) => indexBy('ticket_type_id', group))
  }
}

export function addMembershipToCart(
  event: EventDetails,
  membershipRules: MembershipRules,
  selectedLevel: string,
  promoCodes: Array<string | null | undefined>,
  names: Dict<MembershipAdmitDetails[]>,
  unnamedCounts: Dict<number>,
  surveyAnswers: Dict<Primitive>,
  autoRenew: boolean,
): Promise<void> {
  store.commit('Cart/autoRenewMembership', autoRenew)
  // Ensure the membershipRules are available to create a membership entity from an in-cart membership.
  // @see cartItemToMembership()
  store.commit('membershipRules', membershipRules.rules)
  const result = cartParams(event, selectedLevel, surveyAnswers, promoCodes.filter((code) => code != null) as string[])
  result.event = event
  result.ignoreFeeTickets = true
  result.add = ticketsPayload(membershipRules, selectedLevel, unnamedCounts, names)
  return reserveTickets(result)
}

export function allowMemberReserveBeyondTerm(): boolean {
  const reserveBeyondTerm = environment.config.member_reserve_session_beyond_term
  const autoRenew = store.state['Cart'].autoRenewMembership
  return reserveBeyondTerm === 'always' || (reserveBeyondTerm === 'if_autorenew' && autoRenew)
}
